import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Theme } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import moment from 'moment';

import { Typography } from 'components/v2';
import { LUMA_LOGO } from 'containers/App/constants';
import { useBrandingContext, useNextyContext } from 'containers/App/hooks';

import messages from './messages';
import SupportMessage from './SupportMessage';

const styles = ({
  custom: {
    palette: { colorDarkGray },
  },
  palette: {
    text,
    common: { black },
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    footer: {
      padding: unit + 4,
      fontSize: 9,
      width: '100%',
    },
    footerNexty: {
      padding: unit + 4,
      fontSize: 9,
      width: '100%',
      marginTop: 'auto',
    },
    horizontalText: {
      textAlign: 'center',
      fontSize: 10,
      padding: 0,
    },
    text: {
      color: text.primary,
    },
    link: {
      color: black,
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'none',
      },
    },
    poweredLumaLogo: {
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'start',
      gap: `${unit / 2}px`,
      marginBottom: `${unit * 2}px`,
      position: 'relative',
      width: '45%',
      display: 'flex',
      '& > p': {
        // this is used on the powered logo, that is why it is smaller
        fontSize: '8px',
        color: colorDarkGray,
      },
      '& > img': {
        width: '100%',
      },
    },
    support: {
      fontWeight: 600,
    },
    supportBolder: {
      fontWeight: 700,
    },
  });

export interface CopyrightTextProps {
  classes: any;
  showHorizontalView?: boolean;
}

const getMessageInfo = (message: { label?: string; value?: string } | undefined) => {
  if (message && message.label !== '') {
    return message;
  }
  return null;
};

const CopyrightText: React.FC<CopyrightTextProps> = ({ classes, showHorizontalView }) => {
  const { cobrandingCustomStyles, isCobrandingEnabled } = useBrandingContext();
  const { nextyStorageValue } = useNextyContext();
  const cobrandingUiSettings = cobrandingCustomStyles?.styles?.theme?.uiSettings ?? {};

  const supportMessage = getMessageInfo(cobrandingUiSettings?.support);
  const termsMessage = getMessageInfo(cobrandingUiSettings?.terms);
  const privacyMessage = getMessageInfo(cobrandingUiSettings?.privacy);
  const helpMessage = getMessageInfo(cobrandingUiSettings?.help);
  const supportPhoneMessage = getMessageInfo(cobrandingUiSettings?.supportPhone);

  const hideZenDeskHelp = !cobrandingUiSettings.enableZenDeskChat && isCobrandingEnabled;
  const showPoweredLumaLogo = isCobrandingEnabled && hideZenDeskHelp;

  return (
    <div
      className={classnames({
        [classes.footerNexty]: nextyStorageValue,
        [classes.footer]: !nextyStorageValue,
        [classes.horizontalText]: showHorizontalView,
      })}
    >
      {showPoweredLumaLogo && (
        <div className={classes.poweredLumaLogo}>
          <Typography variant="bW400F10">Powered by</Typography>
          <img src={LUMA_LOGO} alt="Luma Health" />
        </div>
      )}
      <span className={classes.text}>
        &copy; {moment().format('YYYY')} {'Luma Health '}
        {!showHorizontalView && <br />}
        <FormattedMessage {...messages.allRights} />
        {!showHorizontalView && <br />}
        {isCobrandingEnabled ? (
          <>
            {supportMessage && (
              <>
                <span className={classes.support}>
                  <SupportMessage className={classes.link} label={supportMessage.label} url={supportMessage.value} />
                </span>
                {!showHorizontalView && <br />}
              </>
            )}
            {termsMessage && (
              <>
                <span className={classes.support}>
                  <SupportMessage className={classes.link} label={termsMessage.label} url={termsMessage.value} />
                </span>
                {!showHorizontalView && <br />}
              </>
            )}
            {privacyMessage && (
              <>
                <span className={classes.support}>
                  <SupportMessage className={classes.link} label={privacyMessage.label} url={privacyMessage.value} />
                </span>
                {!showHorizontalView && <br />}
              </>
            )}
            {helpMessage && (
              <>
                <span className={classes.support}>
                  <SupportMessage className={classes.link} label={helpMessage.label} url={helpMessage.value} />
                </span>
                {!showHorizontalView && <br />}
              </>
            )}
            {supportPhoneMessage && (
              <span className={classes.support}>
                <SupportMessage
                  className={classes.link}
                  label={`${supportPhoneMessage.label}: ${supportPhoneMessage.value}`}
                  url={`tel:${supportPhoneMessage.value}`}
                />
              </span>
            )}
          </>
        ) : (
          <span className={classnames(classes.support, { [classes.supportBolder]: showHorizontalView })}>
            {showHorizontalView && ' '}
            <FormattedMessage {...messages.support} />
            <a href="tel:(415) 741-3377" className={classes.link}>
              (415) 741-3377
            </a>
          </span>
        )}
      </span>
    </div>
  );
};

export default withStyles(styles)(CopyrightText);
